/**
 * PlayerPageTabsView - Pure presentation component for the player page tab navigation
 *
 * Displays a horizontal tab bar with underline indicator for the active tab.
 * Tabs include: Overview, Data Hub, Reports & Notes, Similar Players
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Download } from "lucide-react-native";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Content-container style for the horizontal tab scroller. `flexGrow: 1` lets
 * the row fill the strip and `justifyContent: center` centres the tabs while
 * they fit; once they overflow (narrow phones) the row keeps its natural width
 * and scrolls horizontally instead of clipping the edge tabs (SE-5446 follow-up).
 */
const TAB_SCROLL_CONTENT = { flexGrow: 1, justifyContent: "center" } as const;

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Available tab options for the player page
 * @remarks `financials` is OWNER-only (SE-4348). Non-owners never see the
 * tab label; the tab content container redirects stale `financials` state
 * to `overview` when `isOwner` is false so reactive-var rehydration after
 * a role change can't leak the tab.
 */
export type PlayerPageTab =
  | "overview"
  | "dataHub"
  | "reportsNotes"
  | "similarPlayers"
  | "financials";

/**
 * Tab configuration with label and key
 */
interface TabConfig {
  key: PlayerPageTab;
  label: string;
}

/**
 * Pre-memoized handlers for each tab press
 */
export type TabPressHandlers = Record<PlayerPageTab, () => void>;

/**
 * Props for PlayerPageTabsView component
 */
interface PlayerPageTabsViewProps {
  activeTab: PlayerPageTab;
  tabPressHandlers: TabPressHandlers;
  isDark: boolean;
  onDownloadReport: () => void;
  isGeneratingPdf?: boolean;
  /** Whether to show the download report button (feature flag controlled) */
  showDownloadReport?: boolean;
  /** Whether this is a manually added player — shows only Reports & Notes tab */
  isManual?: boolean;
  /**
   * Whether the current user is an organization OWNER. When true, the
   * Financials tab is appended to the visible strip (both for provider
   * and manual players). When false the tab is filtered out entirely —
   * non-owners see no UI affordance referencing financials. Defaults to
   * `false` so legacy call sites (tests, storybooks) stay non-owner.
   */
  isOwner?: boolean;
  /** Whether to use compact spacing (used in modal context) */
  isCompact?: boolean;
}

/**
 * Map tab keys to testID constants. Complete as of SE-6876 — `overview` and
 * `dataHub` were the two tabs left without one, which meant a spec covering
 * "every tab opens" could only reach them by label text.
 */
const TAB_TEST_IDS: Record<PlayerPageTab, string> = {
  overview: TEST_ID_PLAYER_DETAIL.OVERVIEW_TAB,
  dataHub: TEST_ID_PLAYER_DETAIL.DATA_HUB_TAB,
  reportsNotes: TEST_ID_PLAYER_DETAIL.REPORTS_NOTES_TAB,
  similarPlayers: TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYERS_TAB,
  financials: TEST_ID_PLAYER_DETAIL.FINANCIALS_TAB,
};

/**
 * Tab configuration array. The Financials tab is appended last so the
 * existing visual order (Overview → Data Hub → Reports & Notes → Similar
 * Players) is preserved for non-owners who see the strip without
 * Financials — filtering by `isOwner` happens at render time.
 */
const TABS: readonly TabConfig[] = [
  { key: "overview", label: "Overview" },
  { key: "dataHub", label: "Data Hub" },
  { key: "reportsNotes", label: "Reports & Notes" },
  { key: "similarPlayers", label: "Similar Players" },
  { key: "financials", label: "Financials" },
] as const;

/**
 * Tabs visible for manually added players. Always includes Reports &
 * Notes; Financials is appended on top of that at render time when
 * `isOwner` is true so Academy OWNERs still see the tab (AC-2 of
 * SE-4348). Pre-computed here so the view doesn't allocate on every
 * render.
 */
const MANUAL_TABS: readonly TabConfig[] = TABS.filter(
  tab => tab.key === "reportsNotes" || tab.key === "financials"
);

/**
 * PlayerPageTabsView renders the tab navigation bar
 * @param props - View props including active tab and pre-memoized handlers
 * @param props.activeTab - Currently selected tab key
 * @param props.tabPressHandlers - Pre-memoized handlers for each tab
 * @param props.isDark - Whether dark mode is active
 * @param props.onDownloadReport - Callback for download report button
 * @param props.isGeneratingPdf - Whether PDF generation is in progress
 * @param props.showDownloadReport - Whether to show the download report button
 * @param props.isManual - Whether this is a manually added player (shows only Reports & Notes)
 * @param props.isOwner - Whether the current user is an OWNER (gates the Financials tab)
 * @param props.isCompact - Whether to use compact spacing (used in modal context)
 * @returns HStack containing tab buttons with underline indicator and download button
 */
const PlayerPageTabsView = ({
  activeTab,
  tabPressHandlers,
  onDownloadReport,
  isGeneratingPdf = false,
  showDownloadReport = false,
  isManual = false,
  isOwner = false,
  isCompact = false,
}: PlayerPageTabsViewProps) => (
  <Stack
    className={`relative w-full border-b border-outline-default ${isCompact ? "pt-1" : "pt-4"}`}
  >
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={TAB_SCROLL_CONTENT}
    >
      <Stack direction="horizontal" className="gap-6 px-4">
        {(isManual ? MANUAL_TABS : TABS)
          .filter(tab => (tab.key === "financials" ? isOwner : true))
          .map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <Pressable
                key={tab.key}
                testID={TAB_TEST_IDS[tab.key]}
                onPress={tabPressHandlers[tab.key]}
              >
                <Stack className={`relative ${isCompact ? "pb-2" : "pb-3"}`}>
                  <Text
                    variant="body-strong"
                    className={`${
                      isActive ? "text-content-primary" : "text-content-muted"
                    }`}
                  >
                    {tab.label}
                  </Text>
                  {isActive && (
                    <Stack className="absolute inset-x-0 -bottom-px h-micro bg-accent-primary" />
                  )}
                </Stack>
              </Pressable>
            );
          })}
      </Stack>
    </ScrollView>
    {showDownloadReport && (
      <Stack className="absolute right-4 top-1/2 -translate-y-1/2">
        <Button
          testID="PLAYER_DETAIL.DOWNLOAD_REPORT_BUTTON"
          label={isGeneratingPdf ? "Generating..." : "Download Report"}
          onPress={onDownloadReport}
          size="sm"
          variant="outline"
          tone="secondary"
          labelTone="primary"
          labelVariant="body"
          icon={Download}
          iconTone="primary"
          isLoading={isGeneratingPdf}
          isDisabled={isGeneratingPdf}
        />
      </Stack>
    )}
  </Stack>
);

PlayerPageTabsView.displayName = "PlayerPageTabsView";

export default memo(PlayerPageTabsView);
